import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReceiptText } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Tabs } from '../../components/nursery/accounts/AccountBits';
import { CreditRequestList } from '../../components/nursery/accounts/CreditRequestList';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { errorMessage } from '../../utils/apiResponse';
import type { CreditNoteRequest, CreditRequestAccess } from '../../types/nurseryAccounts';

/**
 * /accounts/credit-note-requests: credit notes a nursery manager asked for.
 * An organisation admin or an Accounts Admin approves them here, which raises
 * the credit note (Rob, 2026-10-13). Gated by the server's answer, not a
 * licence gate: an org admin with no accounts seat approves too.
 */

type Tab = 'waiting' | 'decided';

function CreditNoteRequests() {
  const [params, setParams] = useSearchParams();
  const highlight = Number(params.get('request')) || null;
  const { hasAccess: hasAccounts } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: hasNursery } = useStockSeat('manage');

  const [access, setAccess] = useState<CreditRequestAccess | null>(null);
  const [requests, setRequests] = useState<CreditNoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('waiting');

  const load = useCallback(async () => {
    setError(null);
    try {
      const a = await nurseryAccountsService.getCreditRequestAccess();
      if (!a.success || !a.data) {
        setError(errorMessage(a, 'Could not check what you can do'));
        return;
      }
      setAccess(a.data);
      if (!a.data.can_approve && !a.data.can_request) return;
      const res = await nurseryAccountsService.getCreditRequests();
      if (res.success && res.data) setRequests(res.data);
      else setError(errorMessage(res, 'Could not load the requests'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the requests'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A notification opens a decided request on the tab that holds it.
  useEffect(() => {
    if (!highlight) return;
    const r = requests.find((x) => x.id === highlight);
    if (!r) return;
    setTab(r.status === 'pending' ? 'waiting' : 'decided');
    requestAnimationFrame(() => document.getElementById(`credit-request-${highlight}`)?.scrollIntoView({ block: 'center' }));
  }, [highlight, requests]);

  const waiting = requests.filter((r) => r.status === 'pending');
  const decided = requests.filter((r) => r.status !== 'pending');
  const shown = tab === 'waiting' ? waiting : decided;

  const invoiceHref = (r: CreditNoteRequest) => {
    if (hasAccounts) return `/accounts/invoices/${r.invoice_id}`;
    if (hasNursery && r.order_id) return `/nursery/orders/${r.order_id}`;
    return null;
  };

  const header = (
    <PageHeader
      title={access?.can_approve ? 'Credit notes to approve' : 'Credit note requests'}
      subtitle="Asked for by a nursery manager, approved by an organisation admin or an Accounts Admin"
      icon={<ReceiptText className="h-6 w-6 text-white" />}
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-3">
          <div className="h-10 bg-neutral-100 rounded-lg animate-pulse" />
          <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />
        </div>
      </>
    );
  }

  if (access && !access.can_approve && !access.can_request) {
    return (
      <>
        {header}
        <div className="max-w-2xl mx-auto px-4 py-10 text-center text-sm text-neutral-600 break-words">
          Credit note requests are for nursery managers, accounts seats and organisation admins.
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        <p className="text-sm text-neutral-700 break-words">
          {access?.can_approve
            ? 'Approving raises the credit note and takes it off what the customer owes. Declining tells the person who asked why. Nobody approves their own.'
            : 'Ask for a credit note from the invoice on its order. It waits here until an organisation admin or an Accounts Admin decides.'}
        </p>
        <Tabs<Tab>
          label="Requests"
          value={tab}
          onChange={(v) => {
            setTab(v);
            if (highlight) setParams({}, { replace: true });
          }}
          tabs={[
            { key: 'waiting', label: `Waiting (${waiting.length})` },
            { key: 'decided', label: 'Decided' },
          ]}
        />
        {shown.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">
            {tab === 'waiting' ? 'Nothing is waiting for approval.' : 'Nothing has been decided yet.'}
          </p>
        ) : (
          <CreditRequestList requests={shown} access={access} onChanged={load} invoiceHref={invoiceHref} highlightId={highlight} />
        )}
      </div>
    </>
  );
}

export default CreditNoteRequests;
